import { c as _c } from "react/compiler-runtime";
import FilterCenterFocus from "@mui/icons-material/FilterCenterFocus";
import ZoomIn from "@mui/icons-material/ZoomIn";
import ZoomOut from "@mui/icons-material/ZoomOut";
import { TabContext, TabList } from "@mui/lab";
import { alpha, Box, Button, IconButton, Stack, Tab, useTheme } from "@mui/material";
import React, { useRef, useState } from "react";
import { hasGraphArgumentsSelector } from "#/entities/dataStructures/node/model/nodeSlice";
import { BenchmarkView } from "#/features/benchmark/ui/BenchmarkView";
import { selectCallstackIsReady } from "#/features/callstack/model/callstackSlice";
import { editorSlice, selectIsEditingNodes } from "#/features/treeViewer/model/editorSlice";
import { PlayerControls } from "#/features/treeViewer/ui/PlayerControls";
import { TreeViewer } from "#/features/treeViewer/ui/TreeViewer";
import { useI18nContext, useSearchParam } from "#/shared/hooks";
import { LoadingSkeletonOverlay } from "#/shared/ui/atoms/LoadingSkeletonOverlay";
import { glassOverlaySx } from "#/shared/ui/styles/glassOverlayStyles";
import { iconButtonHoverSx } from "#/shared/ui/styles/iconButtonHoverStyles";
import { PanelWrapper } from "#/shared/ui/templates/PanelWrapper";
import { PannableViewer } from "#/shared/ui/templates/PannableViewer";
import { StyledTabPanel } from "#/shared/ui/templates/StyledTabPanel";
import { TabListWrapper } from "#/shared/ui/templates/TabListWrapper";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
import { useArgumentsNodeData, usePlayerControls, useViewerPan, type ViewerPanHandle } from "../hooks";
import { resetStructuresState } from "../lib";
type ZoomControlsProps = {
  viewerPanRef: React.RefObject<ViewerPanHandle | null>;
  isViewAtDefault: boolean;
};
const ZoomControls: React.FC<ZoomControlsProps> = t0 => {
  const $ = _c(39);
  const {
    viewerPanRef,
    isViewAtDefault
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] !== theme) {
    t1 = glassOverlaySx(theme);
    $[0] = theme;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      position: "absolute",
      right: 12,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 60,
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      padding: 0.5,
      borderRadius: 2,
      ...t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== viewerPanRef) {
    t3 = () => viewerPanRef.current?.zoomIn();
    $[4] = viewerPanRef;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== theme) {
    t4 = iconButtonHoverSx(theme);
    $[6] = theme;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ZoomIn fontSize="small" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4) {
    t6 = <IconButton title="Zoom in" onClick={t3} size="small" sx={t4}>{t5}</IconButton>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== viewerPanRef) {
    t7 = () => viewerPanRef.current?.zoomOut();
    $[12] = viewerPanRef;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== theme) {
    t8 = iconButtonHoverSx(theme);
    $[14] = theme;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ZoomOut fontSize="small" />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t8) {
    t10 = <IconButton title="Zoom out" onClick={t7} size="small" sx={t8}>{t9}</IconButton>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== theme.palette.divider) {
    t11 = alpha(theme.palette.divider, 0.5);
    $[20] = theme.palette.divider;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <Box sx={{
      height: 1,
      mx: 0.5,
      background: t11
    }} />;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== viewerPanRef) {
    t13 = () => viewerPanRef.current?.resetView();
    $[24] = viewerPanRef;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== theme) {
    t14 = iconButtonHoverSx(theme);
    $[26] = theme;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <FilterCenterFocus fontSize="small" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== isViewAtDefault || $[30] !== t13 || $[31] !== t14) {
    t16 = <IconButton title="Reset view (pan and zoom)" onClick={t13} disabled={isViewAtDefault} size="small" sx={t14}>{t15}</IconButton>;
    $[29] = isViewAtDefault;
    $[30] = t13;
    $[31] = t14;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t10 || $[34] !== t12 || $[35] !== t16 || $[36] !== t2 || $[37] !== t6) {
    t17 = <Box sx={t2}>{t6}{t10}{t12}{t16}</Box>;
    $[33] = t10;
    $[34] = t12;
    $[35] = t16;
    $[36] = t2;
    $[37] = t6;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
};
type ControlsOverlayProps = {
  sliderValue: number;
  setSliderValue: (value: number) => void;
  handlePlay: () => void;
  handleReplay: () => void;
  handleStepBack: () => void;
  handleStepForward: () => void;
  viewerPanRef: React.RefObject<ViewerPanHandle | null>;
  isViewAtDefault: boolean;
};
const ControlsOverlay: React.FC<ControlsOverlayProps> = t0 => {
  const $ = _c(31);
  const {
    sliderValue,
    setSliderValue,
    handlePlay,
    handleReplay,
    handleStepBack,
    handleStepForward,
    viewerPanRef,
    isViewAtDefault
  } = t0;
  const dispatch = useAppDispatch();
  const isEditingNodes = useAppSelector(selectIsEditingNodes);
  const hasGraphArguments = useAppSelector(hasGraphArgumentsSelector);
  const theme = useTheme();
  const {
    saveGraphNodePositions,
    clearGraphNodePositions
  } = useArgumentsNodeData();
  let t1;
  if ($[0] !== dispatch || $[1] !== isEditingNodes || $[2] !== saveGraphNodePositions) {
    t1 = () => {
      if (!isEditingNodes) {
        resetStructuresState(dispatch, false, true);
      } else {
        saveGraphNodePositions();
      }
      dispatch(editorSlice.actions.setIsEditing(!isEditingNodes));
    };
    $[0] = dispatch;
    $[1] = isEditingNodes;
    $[2] = saveGraphNodePositions;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleEditButtonClick = t1;
  let t2;
  if ($[4] !== isViewAtDefault || $[5] !== viewerPanRef) {
    t2 = <ZoomControls viewerPanRef={viewerPanRef} isViewAtDefault={isViewAtDefault} />;
    $[4] = isViewAtDefault;
    $[5] = viewerPanRef;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== clearGraphNodePositions || $[8] !== handleEditButtonClick || $[9] !== hasGraphArguments || $[10] !== isEditingNodes) {
    t3 = hasGraphArguments && <Box sx={{
      position: "absolute",
      top: 62,
      right: 8,
      zIndex: 50
    }}><Stack sx={{
        gap: 1
      }}><Button title={`${isEditingNodes ? "Save" : "Edit"} graph node positions`} color={isEditingNodes ? "success" : "info"} onClick={handleEditButtonClick}>{isEditingNodes ? "Save" : "Edit"}</Button>{isEditingNodes && <Button title="Your changes will be lost" color="warning" onClick={clearGraphNodePositions}>Recalculate</Button>}</Stack></Box>;
    $[7] = clearGraphNodePositions;
    $[8] = handleEditButtonClick;
    $[9] = hasGraphArguments;
    $[10] = isEditingNodes;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== theme) {
    t4 = glassOverlaySx(theme);
    $[12] = theme;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = {
      position: "absolute",
      maxWidth: "94%",
      width: "400px",
      bottom: 0,
      left: "50%",
      transform: "translateX(-50%)",
      borderRadius: "12px 12px 0 0",
      zIndex: 70,
      ...t4,
      borderBottom: "none"
    };
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== handlePlay || $[17] !== handleReplay || $[18] !== handleStepBack || $[19] !== handleStepForward || $[20] !== isEditingNodes || $[21] !== setSliderValue || $[22] !== sliderValue) {
    t6 = <PlayerControls disabled={isEditingNodes} sliderValue={sliderValue} setSliderValue={setSliderValue} handlePlay={handlePlay} handleReplay={handleReplay} handleStepBack={handleStepBack} handleStepForward={handleStepForward} />;
    $[16] = handlePlay;
    $[17] = handleReplay;
    $[18] = handleStepBack;
    $[19] = handleStepForward;
    $[20] = isEditingNodes;
    $[21] = setSliderValue;
    $[22] = sliderValue;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = <Box sx={t5}>{t6}</Box>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t2 || $[28] !== t3 || $[29] !== t7) {
    t8 = <>{t2}{t3}{t7}</>;
    $[27] = t2;
    $[28] = t3;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
};
type TabName = "structure" | "benchmark";
const TabNames = new Set<TabName>(["structure", "benchmark"]);
const isValidTabName = (name: unknown): name is TabName => TabNames.has(name as TabName);
export type TreeViewPanelProps = {
  /** Optional actions to render in the tab header (e.g. output panel collapse on mobile) */
  trailingHeaderActions?: React.ReactNode;
  /** From SplitPanelsLayout - panel vertical size (unused, for type compatibility) */
  verticalSize?: number;
};
export const TreeViewPanel: React.FC<TreeViewPanelProps> = t0 => {
  const $ = _c(62);
  const {
    trailingHeaderActions
  } = t0;
  const {
    LL
  } = useI18nContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      defaultValue: "structure",
      validate: isValidTabName
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tabValue, setTabValue] = useSearchParam("mode", t1);
  const [sliderValue, setSliderValue] = useState(100);
  const isEditingNodes = useAppSelector(selectIsEditingNodes);
  const isCallstackReady = useAppSelector(selectCallstackIsReady);
  const viewerPanRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ref: viewerPanRef
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    handlePanStart,
    handlePanEnd,
    handleMouseMove,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    viewerRef,
    transformRef,
    isViewAtDefault
  } = useViewerPan(t2);
  const {
    handleKeyDown,
    handlePlay,
    handleReplay,
    handleReset,
    handleStepBack,
    handleStepForward
  } = usePlayerControls();
  let t3;
  if ($[2] !== setTabValue) {
    t3 = (_, newValue) => {
      setTabValue(newValue);
    };
    $[2] = setTabValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleTabChange = t3;
  const isReady = isCallstackReady && !isEditingNodes;
  let t4;
  if ($[4] !== handlePlay || $[5] !== handleReplay || $[6] !== handleStepBack || $[7] !== handleStepForward || $[8] !== isViewAtDefault || $[9] !== sliderValue) {
    t4 = <ControlsOverlay sliderValue={sliderValue} setSliderValue={setSliderValue} handlePlay={handlePlay} handleReplay={handleReplay} handleStepBack={handleStepBack} handleStepForward={handleStepForward} viewerPanRef={viewerPanRef} isViewAtDefault={isViewAtDefault} />;
    $[4] = handlePlay;
    $[5] = handleReplay;
    $[6] = handleStepBack;
    $[7] = handleStepForward;
    $[8] = isViewAtDefault;
    $[9] = sliderValue;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const overlay = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      height: "100%"
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LoadingSkeletonOverlay />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      display: "none",
      xs: {
        display: "none"
      },
      md: {
        display: "flex"
      }
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== LL) {
    t8 = LL.PANEL_TABS();
    $[14] = LL;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t10;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Tab label="Structure Viewer" value="structure" />;
    t10 = <Tab label="Benchmark" value="benchmark" />;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  let t11;
  if ($[18] !== handleTabChange || $[19] !== t8) {
    t11 = <TabList onChange={handleTabChange} aria-label={t8}>{t9}{t10}</TabList>;
    $[18] = handleTabChange;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      alignItems: "center"
    };
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== LL || $[23] !== handleReset || $[24] !== isReady || $[25] !== tabValue) {
    t13 = tabValue === "structure" && <Button title={LL.RESET_DATA_STRUCTURES()} disabled={!isReady} color="inherit" onClick={handleReset} sx={{
      height: "100%"
    }}>{LL.RESET()}</Button>;
    $[22] = LL;
    $[23] = handleReset;
    $[24] = isReady;
    $[25] = tabValue;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13 || $[28] !== trailingHeaderActions) {
    t14 = <Stack direction="row" spacing={1} sx={t12}>{trailingHeaderActions}{t13}</Stack>;
    $[27] = t13;
    $[28] = trailingHeaderActions;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t11 || $[31] !== t14) {
    t15 = <TabListWrapper sx={t7}>{t11}{t14}</TabListWrapper>;
    $[30] = t11;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      position: "relative",
      height: "100%",
      p: 0,
      flexGrow: 1
    };
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      height: "100%",
      width: "100%",
      touchAction: "none",
      cursor: "grab"
    };
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== handlePanStart) {
    t18 = ev => {
      if (ev.button === 0) {
        handlePanStart(ev);
      }
    };
    $[35] = handlePanStart;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== sliderValue) {
    t19 = <TreeViewer playbackInterval={sliderValue} />;
    $[37] = sliderValue;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t19 || $[40] !== transformRef) {
    t20 = <PannableViewer transformRef={transformRef}>{t19}</PannableViewer>;
    $[39] = t19;
    $[40] = transformRef;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== handleMouseMove || $[43] !== handlePanEnd || $[44] !== handleTouchEnd || $[45] !== handleTouchMove || $[46] !== handleTouchStart || $[47] !== t18 || $[48] !== t20 || $[49] !== viewerRef) {
    t21 = <div ref={viewerRef} style={t17} onMouseDown={t18} onMouseUp={handlePanEnd} onMouseMove={handleMouseMove} onMouseLeave={handlePanEnd} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>{t20}</div>;
    $[42] = handleMouseMove;
    $[43] = handlePanEnd;
    $[44] = handleTouchEnd;
    $[45] = handleTouchMove;
    $[46] = handleTouchStart;
    $[47] = t18;
    $[48] = t20;
    $[49] = viewerRef;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== overlay || $[52] !== t21) {
    t22 = <StyledTabPanel value="structure" sx={t16} overlay={overlay}>{t21}</StyledTabPanel>;
    $[51] = overlay;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <StyledTabPanel value="benchmark"><BenchmarkView /></StyledTabPanel>;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t15 || $[56] !== t22 || $[57] !== tabValue) {
    t24 = <TabContext value={tabValue}>{t15}{t22}{t23}</TabContext>;
    $[55] = t15;
    $[56] = t22;
    $[57] = tabValue;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== handleKeyDown || $[60] !== t24) {
    t25 = <PanelWrapper sx={t5} onKeyDown={handleKeyDown}>{t6}{t24}</PanelWrapper>;
    $[59] = handleKeyDown;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  return t25;
};